import React from "react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";

export default function PrivacyPage() {
  return (
    <>
      <PageHero
        eyebrow="Privacy Policy"
        title="How we handle your information."
        lead="OGO Notary & Signing Services — a division of OldGuyOutside, LLC — respects the privacy of every signer, client, and visitor to OGONotary.com. This Privacy Policy explains what we collect, why, how we use it, and the choices you have."
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-10 text-slate-300 leading-relaxed">
          <Block title="1. Who We Are">
            <p>
              OGO Notary & Signing Services is the notary, signing agent,
              Remote Online Notarization (RON), and Apostille division of
              OldGuyOutside, LLC, a Florida company based in Brevard County,
              Florida. We provide mobile notarization in Brevard County and
              surrounding Florida communities, and RON and Apostille services
              nationally and internationally where lawful.
            </p>
            <p className="mt-3">
              You can reach us about this policy at{" "}
              <a href="mailto:Greg@ogonotary.com" className="text-[#D4AF37] hover:underline">
                Greg@ogonotary.com
              </a>{" "}
              or{" "}
              <a href="tel:+18555054722" className="text-[#D4AF37] hover:underline">
                (855) 505-4722
              </a>.
            </p>
          </Block>

          <Block title="2. Information We Collect">
            <p>
              We collect only the information needed to provide the service
              you request, comply with applicable law, and respond to you.
            </p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>
                <span className="text-white">Appointment / Contact form:</span>{" "}
                full name, email address, phone number, requested service,
                preferred date/time, and the message you provide.
              </li>
              <li>
                <span className="text-white">Vendor Signup form:</span>{" "}
                company name, contact name, role, work email, phone, website,
                coverage area, expected volume, and notes — used to onboard
                title companies, escrow officers, mortgage brokers, attorneys,
                and other B2B partners.
              </li>
              <li>
                <span className="text-white">Resource / App "Notify Me" forms:</span>{" "}
                your name (or the first part of your email) and email
                address, plus the specific app or resource you asked to be
                notified about.
              </li>
              <li>
                <span className="text-white">Notarial journal records:</span>{" "}
                as required by Florida law, we maintain a notary journal
                that may include signer name, type of identification
                presented, type of document, type of notarial act
                performed, and date — but excludes any identification number
                where prohibited.
              </li>
              <li>
                <span className="text-white">RON session records:</span>{" "}
                for Remote Online Notarization, our state-approved
                technology provider performs identity verification
                (knowledge-based authentication and credential analysis)
                and records the encrypted audio-video session for the
                statutory retention period.
              </li>
              <li>
                <span className="text-white">Apostille documents:</span>{" "}
                we receive and physically handle the documents you send us
                for authentication; we do not retain copies beyond what is
                operationally necessary to deliver the service.
              </li>
              <li>
                <span className="text-white">Site analytics & cookies:</span>{" "}
                OGONotary.com may use first-party and third-party analytics
                to understand visitor behavior in aggregate. These tools may
                set cookies on your device. You can disable cookies in your
                browser; some site functionality may be reduced.
              </li>
            </ul>
          </Block>

          <Block title="3. How We Use Your Information">
            <p>
              We use the information you provide solely to:
            </p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>Respond to your inquiry and schedule appointments</li>
              <li>Perform the requested notarial, signing agent, RON, or Apostille service</li>
              <li>Satisfy notarial journal, RON recording, and Apostille recordkeeping obligations</li>
              <li>Onboard B2B vendors and provide credentials packets on request</li>
              <li>Send you the specific app or resource notification you opted in to receive</li>
              <li>Operate, secure, and improve the site</li>
            </ul>
            <p className="mt-3">
              We do not use your contact information for unrelated marketing.
              We will only email you about a "Notify Me" app or resource you
              specifically requested.
            </p>
          </Block>

          <Block title="4. How We Protect Your Information">
            <p>
              We use commercially reasonable administrative, technical, and
              physical safeguards. RON sessions are encrypted in transit. The
              admin dashboard for viewing inquiries is password-protected
              with a signed, expiring bearer token and is restricted to
              authorized OGO Notary personnel. Paper journals and physical
              documents are stored securely.
            </p>
          </Block>

          <Block title="5. Sharing">
            <p>
              <span className="text-white">We do not sell your personal information.</span>{" "}
              We share information only as needed to provide the requested
              service (for example, with the title or escrow company that
              engaged us, or the Florida Secretary of State for Apostille
              authentication) or as required by law, court order, or
              government request.
            </p>
          </Block>

          <Block title="6. Retention">
            <p>
              Personal information collected via our forms is retained for as
              long as needed to provide the service and to satisfy our legal,
              accounting, and recordkeeping obligations. Notarial journal
              records and RON audio-video recordings are retained for the
              minimum periods required by Florida law and applicable rules.
            </p>
          </Block>

          <Block title="7. Your Choices">
            <p>
              You may request access to, correction of, or deletion of your
              personal information by emailing us at{" "}
              <a href="mailto:Greg@ogonotary.com" className="text-[#D4AF37] hover:underline">
                Greg@ogonotary.com
              </a>
              , subject to legal and regulatory retention obligations
              applicable to notarial journals and RON recordings. You may
              also unsubscribe from any "Notify Me" list at any time.
            </p>
          </Block>

          <Block title="8. Children">
            <p>
              OGONotary.com is not directed to children under 13, and we do
              not knowingly collect personal information from children. If
              you believe a child has provided us with personal information,
              please contact us so we can delete it.
            </p>
          </Block>

          <Block title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Material
              changes will be posted on this page with an updated effective
              date below.
            </p>
          </Block>

          <Block title="10. Contact">
            <p>
              Questions about this Privacy Policy? Reach out via the Contact
              page on OGONotary.com or directly at{" "}
              <a href="mailto:Greg@ogonotary.com" className="text-[#D4AF37] hover:underline">
                Greg@ogonotary.com
              </a>
              .
            </p>
          </Block>
          <p className="text-xs text-slate-500">
            Effective date: 2026. OGO Notary & Signing Services · An
            OldGuyOutside, LLC Company. This Privacy Policy is provided as a
            general information template and does not constitute legal advice.
          </p>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <SectionEyebrow className="mb-3">{title}</SectionEyebrow>
      <div className="text-slate-300 leading-relaxed">{children}</div>
    </div>
  );
}
